import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import {
  Drawer,
  DrawerProps,
  Button,
  Form,
  Stack,
  Uploader,
  Message,
  Loader,
  useToaster,
  Schema,
  SelectPicker,
  DatePicker,
  IconButton,
  Divider,
  Checkbox,
  InputPicker,
  Container,
  Radio,
  DOMHelper,
  Whisper,
  Popover,
  PanelGroup,
  Panel
} from 'rsuite';
import AvatarIcon from '@rsuite/icons/legacy/Avatar';

import { addUser, getAdminUsers, updateUsersMass, getManagers } from '@/services/user.service';
import { getGrades } from '@/services/grade.service';
import { getServicelines } from '@/services/serviceline.service';
import { getWfhProfiles } from '@/services/wfhProfile.service';
import { getDesks } from '@/services/desk.service';
import { getSpots } from '@/services/spot.service';
import { getAccessProfiles } from '@/services/accessProfile.service';
import { useAppDispatch, useAppSelector } from '@/config/hooks';
import { getCurrentUser } from '@/services/auth.service';
import { Icon } from '@rsuite/icons';
import { VscSave } from 'react-icons/vsc';
import { FaRegBuilding, FaRegUser } from 'react-icons/fa';
import { MdOutlineHomeWork } from 'react-icons/md';
import { LuUserCog } from 'react-icons/lu';

const { StringType } = Schema.Types;

const model = Schema.Model({
  firstname: StringType().isRequired('This field is required.'),
  lastname: StringType().isRequired('This field is required.'),
  registrationNumber: StringType().isRequired('This field is required.'),
  Email: StringType()
    .isEmail('Please enter a valid email address.')
    .isRequired('This field is required.'),
  grade: StringType().isRequired('This field is required.'),
  serviceLine: StringType().isRequired('This field is required.')
});

function previewFile(file, callback) {
  const reader = new FileReader();
  reader.onloadend = () => {
    callback(reader.result);
  };
  reader.readAsDataURL(file);
}

const DrawerUpdate = (props: DrawerProps) => {
  const { onClose, checkedKeys, setCheckedKeys, ...rest } = props;

  const toaster = useToaster();

  const dispatch = useAppDispatch();
  const currentUser = getCurrentUser();
  const { getHeight } = DOMHelper;

  const loading = useAppSelector((state: any) => state.users.loading);

  const users = useAppSelector((state: any) => state.users.data)?.map(item => {
    return { value: item._id, label: item.firstname + ' ' + item.lastname };
  });
  const managers = useAppSelector((state: any) => state.users.managers) || [];
  const mappedManagers = Array.isArray(managers)
    ? managers.map(item => ({
        value: item._id,
        label: `${item.firstname} ${item.lastname}`
      }))
    : [];
  const filteredSecondManagers = mappedManagers.filter(
    manager => manager.value !== (userData?.manager?._id || userData?.manager)
  );

  const features = useAppSelector((state: any) => state.feature.data);

  const grades = useAppSelector((state: any) => state.grade.data)?.map(item => {
    return { value: item._id, label: item.grade_name };
  });

  const servicelines = useAppSelector((state: any) => state.serviceLine.data)?.map(item => {
    return { value: item._id, label: item.serviceLine, SubServiceLines: item.SubServiceLines };
  });

  const wfhProfiles = useAppSelector((state: any) => state.wfhProfile.data)?.map(item => {
    return { ...item, value: item._id, label: item.name };
  });

  const accessProfiles = useAppSelector((state: any) => state.accessProfile.data)?.map(item => {
    return { ...item, value: item._id, label: item.name };
  });

  const [userData, setUserData] = useState<any>({});
  const [subservicelines, setSubservicelines] = useState([]);

  useEffect(() => {
    dispatch(getAdminUsers());
    dispatch(getManagers());
    dispatch(getGrades());
    dispatch(getServicelines());
    dispatch(getWfhProfiles());
    dispatch(getAccessProfiles());
  }, []);

  useEffect(() => {
    if (userData?.serviceLine) {
      const subservicelines = servicelines.find(
        item => item.value == userData?.serviceLine
      )?.SubServiceLines;
      const values = subservicelines?.map(item => {
        return { value: item, label: item };
      });
      setSubservicelines(values);
    }
  }, [userData]);
  const { t } = useTranslation();

  const profiles = [
    { label: t('Facility Administrator'), value: 'facility' },
    { label: t('DAF Administrator'), value: 'daf' },
    { label: t('Super Administrator'), value: 'super' }
  ];
  const changeUser = (field, value) => {
    setUserData(prevData => ({ ...prevData, [field]: value }));
  };

  const speaker = (
    <Popover
      title={
        wfhProfiles.find(item => item.value == (userData.wfhProfile?._id || userData.wfhProfile))
          ?.label
      }
    >
      <p>{t('Authorized days:')} </p>
      {wfhProfiles
        .find(item => item.value == (userData.wfhProfile?._id || userData.wfhProfile))
        ?.authorizedWfhDays?.map((day, index) => (
          <p key={index}>{day}</p>
        ))}
    </Popover>
  );

  const renderMenuItem = (label, item) => {
    const speaker = (
      <Popover title={item.label || 'WFH Profile'}>
        <p>{t('Authorized days:')}</p>
        {item.authorizedWfhDays?.length > 0 ? (
          item.authorizedWfhDays.map((day, index) => <p key={index}>{day}</p>)
        ) : (
          <p>{t('No authorized days')}</p>
        )}
      </Popover>
    );

    return (
      <Whisper
        placement="top"
        trigger="hover"
        speaker={speaker}
        controlId={`control-id-${item.value}`}
        key={item.value}
      >
        <div>{label}</div>
      </Whisper>
    );
  };

  const speakerAccess = (
    <Popover
      title={
        accessProfiles.find(item => item.value == (userData.Group?._id || userData.Group))?.label
      }
    >
      <p>Zones: </p>
      {accessProfiles
        .find(item => item.value == (userData.Group?._id || userData.Group))
        ?.zones?.map((zone, index) => (
          <p key={index}>{zone.name}</p>
        ))}
    </Popover>
  );

  const renderAccessItem = (label, item) => {
    const speaker = (
      <Popover title={item.label || 'Access Profile'}>
        <p>{t('Zones:')}</p>
        {item.zones?.length > 0 ? (
          item.zones.map((zone, index) => <p key={index}>{zone.name}</p>)
        ) : (
          <p>{t('No accessible zones')}</p>
        )}
      </Popover>
    );

    return (
      <Whisper
        placement="top"
        trigger="hover"
        speaker={speaker}
        controlId={`control-id-${item.value}`}
        key={item.value}
      >
        <div>{label}</div>
      </Whisper>
    );
  };

  const handleSave = async () => {
    try {
      await dispatch(
        updateUsersMass({
          user: userData,
          ids: checkedKeys
        })
      )
        .then(() => {
          onClose();
          setCheckedKeys([]);
          toaster.push(
            <Message type="success" duration={5000} showIcon>
              {t('Updated successfully')}
            </Message>,
            { placement: 'topCenter' }
          );
        })
        .catch(e => {
          toaster.push(
            <Message type="error" duration={5000} showIcon>
              {t(e.message)}
            </Message>,
            { placement: 'topCenter' }
          );
        });
    } catch (error) {
      console.error('Erreur lors de la soumission :', error);
    }
  };

  return (
    <Drawer backdrop="static" size="lg" placement="right" onClose={onClose} {...rest}>
      <Form fluid model={model}>
        <Drawer.Header>
          <Drawer.Title>{t('Update users')}</Drawer.Title>
          <Drawer.Actions>
            <IconButton
              loading={loading}
              onClick={handleSave}
              appearance="primary"
              icon={<Icon as={VscSave} />}
            >
              {t('Save')}
            </IconButton>
            <Button onClick={onClose} appearance="subtle">
              {t('Cancel')}
            </Button>
          </Drawer.Actions>
        </Drawer.Header>

        <Drawer.Body style={{ height: Math.max(getHeight(window) - 100, 400), overflow: 'auto' }}>
          <Container>
            <PanelGroup>
              <Panel
                header={
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <FaRegUser style={{ fontSize: '16px' }} />
                    <span>{t('User Information')}</span>
                  </div>
                }
              >
                <Stack spacing={20} wrap style={{ marginBottom: 20 }}>
                  <Form.Group>
                    <Form.ControlLabel>
                      {t('Grade')} <span style={{ color: 'red', marginLeft: '2px' }}>*</span>
                    </Form.ControlLabel>
                    <Form.Control
                      accepter={SelectPicker}
                      name="grade"
                      data={grades}
                      style={{ width: 200 }}
                      value={userData?.grade?._id || userData?.grade}
                      onChange={val => {
                        changeUser('grade', val);
                      }}
                      placement="auto"
                    />
                  </Form.Group>
                  <Form.Group>
                    <Form.ControlLabel>
                      {t('Team')} <span style={{ color: 'red', marginLeft: '2px' }}>*</span>
                    </Form.ControlLabel>

                    <Form.Control
                      accepter={SelectPicker}
                      name={t('serviceLine')}
                      placement="auto"
                      data={servicelines}
                      style={{ width: 200 }}
                      value={userData?.serviceLine?._id || userData?.serviceLine}
                      onChange={val => {
                        changeUser('serviceLine', val);
                      }}
                    />
                  </Form.Group>
                  {/* {subservicelines?.length > 0 && (
                    <Form.Group>
                      <Form.ControlLabel>Sub-Department</Form.ControlLabel>
                      <SelectPicker
                        placement="auto"
                        data={subservicelines}
                        style={{ width: 200 }}
                        value={userData?.subServiceLine?._id || userData?.subServiceLine}
                        onChange={val => {
                          changeUser('subServiceLine', val);
                        }}
                      />
                    </Form.Group>
                  )} */}

                  <Form.Group>
                    <Form.ControlLabel>{t('Manager')}</Form.ControlLabel>
                    <SelectPicker
                      placement="auto"
                      data={mappedManagers}
                      style={{ width: 200 }}
                      value={userData?.manager?._id || userData?.manager}
                      onChange={val => {
                        changeUser('manager', val);
                      }}
                    />
                  </Form.Group>
                  <Form.Group>
                    <Form.ControlLabel>{t('Second Manager')}</Form.ControlLabel>
                    <SelectPicker
                      placement="auto"
                      data={filteredSecondManagers}
                      style={{ width: 200 }}
                      value={userData?.secondValidator?._id || userData?.secondValidator}
                      onChange={val => {
                        changeUser('secondValidator', val);
                      }}
                    />
                  </Form.Group>
                  <Form.Group>
                    <Form.ControlLabel>{t('Hire date')}</Form.ControlLabel>
                    <DatePicker
                      format="dd-MM-yyyy"
                      placement="auto"
                      value={userData.hire_date ? new Date(userData.hire_date) : null}
                      style={{ width: 200 }}
                      onChange={val => {
                        changeUser('hire_date', val);
                      }}
                    />
                  </Form.Group>
                </Stack>
              </Panel>
              {features?.find(item => item.name?.toLowerCase() == 'wfh') && (
                <Panel
                  header={
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <MdOutlineHomeWork style={{ fontSize: '16px' }} />
                      <span>{t('Remote working')}</span>
                    </div>
                  }
                >
                  <Stack spacing={20} wrap style={{ marginBottom: 20 }}>
                    <Whisper
                      placement="top"
                      trigger="hover"
                      controlId="control-id-hover"
                      speaker={speaker}
                    >
                      <Form.Group>
                        <Form.ControlLabel>{t('WFH Profile')}</Form.ControlLabel>
                        <SelectPicker
                          placement="auto"
                          data={mappedManagers}
                          style={{ width: 200 }}
                          value={userData?.wfhProfile?._id || userData?.wfhProfile}
                          onChange={val => {
                            changeUser('wfhProfile', val);
                          }}
                          renderMenuItem={(label, item) => renderMenuItem(label, item)} // Rendu personnalisé des items
                        />
                      </Form.Group>
                    </Whisper>
                    <Form.Group>
                      <Form.ControlLabel>{t('Part Time')}</Form.ControlLabel>
                      <Form.Control
                        name={t('partTime')}
                        style={{ width: 200 }}
                        value={userData?.partTime}
                        onChange={val => {
                          changeUser('partTime', val);
                        }}
                      />
                    </Form.Group>
                  </Stack>
                </Panel>
              )}
              {features?.find(item => item.name?.toLowerCase() == 'on site') && (
                <Panel
                  header={
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <FaRegBuilding style={{ fontSize: '16px' }} />
                      <span>{t('Flex office')}</span>
                    </div>
                  }
                >
                  <Stack spacing={20} wrap style={{ marginBottom: 20 }}>
                    <Whisper
                      placement="top"
                      trigger="hover"
                      controlId="control-id-hover"
                      speaker={speakerAccess}
                    >
                      <Form.Group>
                        <Form.ControlLabel>{t('Access profile')}</Form.ControlLabel>
                        <SelectPicker
                          placement="auto"
                          data={accessProfiles}
                          style={{ width: 200 }}
                          value={userData?.Group?._id || userData?.Group}
                          onChange={val => {
                            changeUser('Group', val);
                          }}
                          renderMenuItem={(label, item) => renderAccessItem(label, item)}
                        />
                      </Form.Group>
                    </Whisper>
                  </Stack>
                </Panel>
              )}

              <Panel
                header={
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <LuUserCog style={{ fontSize: '16px' }} />
                    <span>Roles</span>
                  </div>
                }
              >
                <Stack spacing={20} wrap style={{ marginBottom: 20 }}>
                  <Stack spacing={10}>
                    <Radio
                      value="user"
                      checked={userData.role != 'trainee'}
                      onChange={() => {
                        changeUser('role', 'user');
                      }}
                    >
                      {t('Employee')}
                    </Radio>
                    <Radio
                      value="trainee"
                      checked={userData.role == 'trainee'}
                      onChange={() => {
                        changeUser('role', 'trainee');
                        changeUser('isAdmin', false);
                        changeUser('isManager', false);
                        changeUser('isValidator', false);
                        changeUser('admin', null);
                      }}
                    >
                      {t('Trainee')}
                    </Radio>
                  </Stack>

                  <Checkbox
                    onChange={() => {
                      changeUser('isManager', !userData.isManager);
                    }}
                    checked={userData.isManager}
                    disabled={userData.role == 'trainee'}
                  >
                    {t('Is Manager')}
                  </Checkbox>
                  <Checkbox
                    onChange={() => {
                      changeUser('isValidator', !userData.isValidator);
                    }}
                    checked={userData.isValidator}
                    disabled={userData.role == 'trainee'}
                  >
                    {t('Is Validator')}
                  </Checkbox>
                  <Checkbox
                    onChange={() => {
                      changeUser('isGFSF', !userData.isGFSF);
                    }}
                    checked={userData.isGFSF}
                    disabled={userData.role == 'trainee'}
                  >
                    {t('Is GFSF')}
                  </Checkbox>
                  <Checkbox
                    onChange={() => {
                      changeUser('isSST', !userData.isSST);
                    }}
                    checked={userData.isSST}
                    disabled={userData.role == 'trainee'}
                  >
                    {t('Is SST')}
                  </Checkbox>
                  {currentUser?.admin == 'super' && (
                    <>
                      <Checkbox
                        onChange={() => {
                          if (userData.isAdmin) {
                            changeUser('admin', null);
                          }
                          changeUser('isAdmin', !userData.isAdmin);
                        }}
                        checked={userData.isAdmin}
                        disabled={userData.role == 'trainee'}
                      >
                        {t('Administrator')}
                      </Checkbox>
                      <InputPicker
                        placement="auto"
                        data={profiles}
                        style={{ width: 200 }}
                        disabled={!userData.isAdmin}
                        value={userData.admin}
                        onChange={e => {
                          changeUser('admin', e);
                        }}
                      />
                    </>
                  )}
                </Stack>
              </Panel>
            </PanelGroup>
          </Container>
        </Drawer.Body>
      </Form>
    </Drawer>
  );
};

export default DrawerUpdate;
